import { AxiosInstance } from 'axios'
import {
  GetSchedulesParams,
  PaymentSchedule,
  PaymentScheduleConfiguration,
  PeriodSchedulePreview,
  SchedulePreviewParams
} from 'types/schedules'
import { SCHEDULES_ENDPOINTS } from 'api/ApiRoutes'

class SchedulesApi {
  constructor(private axiosInstance: AxiosInstance) {}

  getPaymentSchedules = async (params?: Partial<GetSchedulesParams>) => {
    const response = await this.axiosInstance.get<PaymentSchedule[]>(
      SCHEDULES_ENDPOINTS.BASE,
      {
        params
      }
    )
    return response.data
  }

  getPaymentSchedule = async ({ id }: { id: PaymentSchedule['id'] }) => {
    const response = await this.axiosInstance.get<PaymentSchedule>(
      SCHEDULES_ENDPOINTS.PAY_SCHEDULE(id)
    )
    return response.data
  }

  updatePaymentSchedule = async ({
    id,
    configuration,
    name
  }: {
    id: PaymentSchedule['id']
    configuration: Exclude<Partial<PaymentScheduleConfiguration>, 'id'>
    name?: string
  }) => {
    const d = await this.axiosInstance.put<PaymentSchedule>(
      SCHEDULES_ENDPOINTS.PAY_SCHEDULE(id),
      {
        pay_schedule: {
          name,
          configuration
        }
      }
    )
    return d.data
  }

  createPaymentSchedule = async ({
    name,
    kind,
    configuration
  }: {
    configuration: Exclude<Partial<PaymentScheduleConfiguration>, 'id'>
    name?: string
    kind: PaymentSchedule['kind']
  }) => {
    const d = await this.axiosInstance.post<PaymentSchedule>(
      SCHEDULES_ENDPOINTS.BASE,
      {
        pay_schedule: {
          name,
          kind,
          configuration
        }
      }
    )
    return d.data
  }

  getPaymentSchedulePreview = async ({
    filters
  }: {
    filters: SchedulePreviewParams
  }) => {
    const {
      schedule_frequency,
      kind,
      biweekly_day_of_week_start,
      period_start,
      period_end,
      period,
      approve_deadline_days,
      submission_deadline_days,
      pay_deadline_days,
      period_date_anchor
    } = filters ?? {}
    const formData = new FormData()
    formData.append(
      'pay_schedule[configuration][frequency]',
      schedule_frequency
    )
    formData.append('pay_schedule[kind]', kind)
    if (biweekly_day_of_week_start) {
      formData.append(
        'pay_schedule[configuration][biweekly_day_of_week_start]',
        biweekly_day_of_week_start
      )
    }
    if (approve_deadline_days || approve_deadline_days === 0) {
      formData.append(
        'pay_schedule[configuration][approve_deadline_days]',
        approve_deadline_days.toString()
      )
    }

    if (submission_deadline_days || submission_deadline_days === 0) {
      formData.append(
        'pay_schedule[configuration][submission_deadline_days]',
        submission_deadline_days.toString()
      )
    }
    if (pay_deadline_days || pay_deadline_days === 0) {
      formData.append(
        'pay_schedule[configuration][pay_deadline_days]',
        pay_deadline_days.toString()
      )
    }
    if (period_date_anchor) {
      formData.append(
        'pay_schedule[configuration][period_date_anchor]',
        period_date_anchor
      )
    }
    if (period_start && !period) {
      formData.append('preview[period_start]', period_start)
    }
    if (period_end && !period) {
      formData.append('preview[period_end]', period_end)
    }
    if (period) {
      formData.append('preview[period]', period)
    }

    const urlString = new URLSearchParams(formData as never).toString()

    const d = await this.axiosInstance.get<PeriodSchedulePreview>(
      `${SCHEDULES_ENDPOINTS.NEW_SCHEDULE}?${urlString}`
    )
    return d.data
  }
}

export default SchedulesApi
